import { motion } from "framer-motion";
import { Crown, Star, Sparkles, Phone, Music, Users, Wine, Disc, MapPin, Clock } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";

const venueImages = [
  {
    id: 1,
    url: "https://images.unsplash.com/photo-1627224399635-04b73da49323?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    alt: "Pole dance performance First Lady Club"
  },
  {
    id: 2,
    url: "https://images.unsplash.com/photo-1540086015396-f0b458832b38?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
    alt: "VIP table service Kyrenia"
  },
  {
    id: 3,
    url: "https://images.unsplash.com/photo-1578760427294-9871d8667bf3?crop=entropy&cs=srgb&fm=jpg&w=400&q=80",
    alt: "Nightclub atmosphere Cyprus"
  },
  {
    id: 4,
    url: "https://images.pexels.com/photos/5152595/pexels-photo-5152595.jpeg?auto=compress&cs=tinysrgb&w=400",
    alt: "Dance floor entertainment"
  }
];

const GirneCatalogSection = () => {
  const { t, language } = useLanguage();
  const whatsappLink = "https://wa.me/905488484828?text=" + encodeURIComponent(t("floatingWhatsapp.defaultMessage"));

  const features = [
    {
      icon: Sparkles,
      titleKey: "venue.feature1Title",
      descKey: "venue.feature1Desc",
      color: "#FF00FF"
    },
    {
      icon: Star,
      titleKey: "venue.feature2Title",
      descKey: "venue.feature2Desc",
      color: "#D4AF37"
    },
    {
      icon: Wine,
      titleKey: "venue.feature3Title",
      descKey: "venue.feature3Desc",
      color: "#00FFFF"
    },
    {
      icon: Disc,
      titleKey: "venue.feature4Title",
      descKey: "venue.feature4Desc",
      color: "#FF00FF"
    }
  ];

  return (
    <section id="venue" className="py-24 bg-gradient-to-b from-[#0A0A0A] to-[#050505] relative overflow-hidden" data-testid="venue-section">
      {/* Background Effects */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#D4AF37]/10 rounded-full blur-[200px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#FF00FF]/10 rounded-full blur-[200px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header - More Prominent */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/50">
            <Crown size={16} className="text-[#D4AF37]" />
            <span className="text-xs font-bold tracking-wider text-[#D4AF37] uppercase">{t("venue.subtitle")}</span>
          </div>
          
          <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">{t("venue.title1")}</span>{" "}
            <span className="text-[#D4AF37] neon-text-gold">{t("venue.title2")}</span>
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto text-lg leading-relaxed">
            {t("venue.description")}
          </p>
          
          {/* Location & Hours Badge */}
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10">
              <MapPin size={16} className="text-[#FF00FF]" />
              <span className="text-sm text-gray-300">Girne Merkez</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10">
              <Clock size={16} className="text-[#00FFFF]" />
              <span className="text-sm text-gray-300">{t("venue.hours")}</span>
            </div>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Left: Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 gap-4"
          >
            {venueImages.map((img, index) => (
              <div 
                key={img.id} 
                className={`relative overflow-hidden group ${index === 0 ? 'row-span-2' : ''}`}
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute inset-0 border-2 border-[#D4AF37]/0 group-hover:border-[#D4AF37]/50 transition-colors duration-300" />
                
                {/* First image special badge */}
                {index === 0 && (
                  <div className="absolute top-4 left-4 bg-[#D4AF37] text-black px-3 py-1 font-bold text-xs uppercase">
                    Live Shows
                  </div>
                )}
              </div>
            ))}
          </motion.div>

          {/* Right: Features */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 p-5 hover:border-[#D4AF37]/30 transition-colors group"
                data-testid={`venue-feature-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-14 h-14 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: feature.color }}
                  >
                    <feature.icon size={28} className="text-black" />
                  </div>
                  <div>
                    <h3 className="font-['Syne'] text-lg font-bold text-white mb-1 uppercase">
                      {t(feature.titleKey)}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {t(feature.descKey)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* CTA Banner - More Prominent */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-[#D4AF37]/30 via-[#FF00FF]/20 to-[#D4AF37]/30 border-2 border-[#D4AF37]/50 p-8 md:p-12"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-['Syne'] text-3xl md:text-4xl font-bold text-white mb-4 uppercase">
                {t("venue.ctaTitle")}
              </h3>
              <p className="text-gray-200 mb-6 text-lg">
                {t("venue.ctaDesc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-[#25D366] text-white px-8 py-4 font-bold text-lg tracking-wider uppercase hover:scale-105 transition-all shadow-[0_0_40px_rgba(37,211,102,0.4)]"
                  data-testid="venue-whatsapp-btn"
                >
                  <FaWhatsapp size={26} />
                  <span>{t("venue.ctaWhatsapp")}</span>
                </a>
                <a
                  href="tel:+905488484828"
                  className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 font-bold text-lg tracking-wider uppercase hover:scale-105 transition-all"
                  data-testid="venue-call-btn"
                >
                  <Phone size={24} />
                  <span>{t("venue.ctaCall")}</span>
                </a>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-black/40 p-6 text-center border border-[#D4AF37]/30">
                <div className="font-['Bebas_Neue'] text-4xl text-[#D4AF37]">#1</div>
                <p className="text-gray-300 text-sm mt-1">
                  {language === "tr" ? "Girne'de" : "In Kyrenia"}
                </p>
              </div>
              <div className="bg-black/40 p-6 text-center border border-[#FF00FF]/30">
                <div className="font-['Bebas_Neue'] text-4xl text-[#FF00FF]">Live</div>
                <p className="text-gray-300 text-sm mt-1">
                  {language === "tr" ? "Showlar" : "Shows"}
                </p>
              </div>
              <div className="bg-black/40 p-6 text-center border border-[#00FFFF]/30">
                <div className="font-['Bebas_Neue'] text-4xl text-[#00FFFF]">VIP</div>
                <p className="text-gray-300 text-sm mt-1">
                  {language === "tr" ? "Masalar" : "Tables"}
                </p>
              </div>
              <div className="bg-black/40 p-6 text-center border border-[#D4AF37]/30">
                <div className="font-['Bebas_Neue'] text-4xl text-[#D4AF37]">7/24</div>
                <p className="text-gray-300 text-sm mt-1">
                  {language === "tr" ? "Destek" : "Support"}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* SEO Text */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 text-sm max-w-4xl mx-auto leading-relaxed">
            {t("venue.seoText")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default GirneCatalogSection;
